import React, { useEffect, useRef, useState } from "react";
import { LogIn, Send, ShieldAlert, ShieldCheck, UserCog } from "lucide-react";
import RoomCard from "./RoomCard";
import PlayerProfilePreview from "./PlayerProfilePreview";

export default function RoomChat({
  isAuthenticated,
  messages = [],
  currentPlayerUid,
  feedback = "",
  onSendMessage,
  disabled = false,
  categoryName = "la room",
}) {
  const [draft, setDraft] = useState("");
  const chatListRef = useRef(null);

  useEffect(() => {
    const el = chatListRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = (event) => {
    event.preventDefault();

    const value = draft.trim();
    if (!value) {
      return;
    }

    const sent = onSendMessage?.(value);
    if (sent) {
      setDraft("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  if (!isAuthenticated) {
    return (
      <RoomCard
        title="Chat"
        subtitle={`Connecte-toi pour discuter dans ${categoryName}.`}
        contentClassName="space-y-4"
      >
        <div className="rounded-[24px] border border-cyan-100 bg-[linear-gradient(145deg,#ecfeff,#ffffff)] p-5 shadow-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">
            <LogIn className="h-4 w-4" />
            Connexion requise
          </div>
          <h3 className="mt-4 text-xl font-semibold text-slate-950">
            Rejoins la conversation
          </h3>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Le chat est réservé aux comptes connectés pour garder la partie
            lisible et limiter la triche.
          </p>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
            Tu peux continuer à jouer en invité; seuls les messages sont
            verrouillés.
          </div>
        </div>
      </RoomCard>
    );
  }

  return (
    <RoomCard
      title="Chat"
      contentClassName="space-y-4"
    >
      <div ref={chatListRef} className="max-h-[520px] min-h-[260px] overflow-y-auto space-y-1 pr-1">
        {messages.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
            Aucun message pour l'instant dans cette room.
          </div>
        ) : (
          messages.map((message) => {
            if (message.type === "system") {
              return (
                <div
                  key={message.id}
                  className="flex items-center gap-2 px-2 py-1 text-center text-xs italic text-slate-400"
                >
                  <div className="h-px flex-1 bg-slate-200" />
                  <span>{message.text}</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              );
            }

            const isMine =
              String(message.uid || "") === String(currentPlayerUid || "");

            return (
              <div
                key={message.id}
                className={`flex flex-col ${isMine ? "items-end" : "items-start"} px-1`}
              >
                {!isMine && (
                  <div className="mb-0.5 flex items-center gap-1 px-2">
                    <PlayerProfilePreview
                      player={message}
                      avatar={null}
                      isCurrentPlayer={false}
                      nameClassName="text-xs font-semibold text-slate-500"
                    />
                    {message.is_staff ? (
                      <span title="Administrateur" className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700">
                        <UserCog className="h-2 w-2" />
                      </span>
                    ) : message.is_moderator ? (
                      <span title="Moderateur" className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                        <ShieldCheck className="h-2 w-2" />
                      </span>
                    ) : null}
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-5 ${
                    isMine
                      ? "rounded-br-sm bg-cyan-700 text-white"
                      : "rounded-bl-sm bg-slate-100 text-slate-900"
                  }`}
                >
                  {message.text}
                </div>
              </div>
            );
          })
        )}
      </div>

      {feedback ? (
        <div
          className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{feedback}</span>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-3">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          maxLength={240}
          disabled={disabled}
          placeholder="Envoyer un message à la room..."
          className="w-full resize-none rounded-3xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-100 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-slate-400">{draft.trim().length}/240</div>
          <button
            type="submit"
            disabled={disabled || !draft.trim()}
            className="inline-flex items-center gap-2 rounded-2xl bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:bg-cyan-300"
          >
            <Send className="h-4 w-4" />
            Envoyer
          </button>
        </div>
      </form>
    </RoomCard>
  );
}
